/*
 * Klassifizierungs-Modal für einen Netzwerk-Scan-Kandidaten (concept
 * C.10.a Phase B / Slot 3). Operator wählt Protokoll aus den vom
 * Scanner vermuteten Vorschlägen, gibt Symbol + Anzeigename ein,
 * POST /api/plants/{p}/scan/candidates/{id}/classify legt das Device an
 * und verknüpft den Kandidaten. Idempotent auf der Server-Seite
 * (Deterministic-UUIDv5-Device-Id) — re-classifying updated protocol/name.
 */

import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { IconCancel } from '../icons/HvacIcons';
import './hv-modal.css';

interface Props {
  plantId: string;
  candidate: {
    id: string;
    ip: string;
    mac: string | null;
    hostname: string | null;
    vendorHint: string | null;
    protocolHints: string[];
  };
  onClassified(deviceId: string, protocol: string): void;
  onClose(): void;
}

// Muss mit ClassifyScanCandidateHandler.KnownProtocols und mit den Treiber-
// Namen des Kotlin-Panel-Agents (drivers/*.kt, `override val name`) im
// Gleichschritt bleiben. `http` ist bewusst NICHT mehr in der Liste: es ist
// der veraltete Alias von `http-json`, den der Server beim Klassifizieren
// normalisiert (DeviceCredentialSchemaRegistry.Canonical) — anbieten würde
// heißen, zwei Namen für ein Protokoll in den Datenbestand zu schreiben.
// Kein `bacnet-mstp`: MS/TP ist eine Transport-Variante von `bacnet-ip`.
const KNOWN_PROTOCOLS = [
  'bacnet-ip', 'modbus-tcp', 'modbus-rtu', 'opc-ua', 'climatix',
  'knx-ip', 'mbus', 'bsb', 'http-json', 'ozw',
] as const;

/** Suggest a symbol from vendor + last IP octet. Fallback: last octet only. */
function suggestSymbol(candidate: Props['candidate']): string {
  const octet = candidate.ip.split('.').pop() ?? 'device';
  const vendor = candidate.vendorHint?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return vendor ? `${vendor}-${octet}` : `dev-${octet}`;
}

export function ClassifyCandidateModal({ plantId, candidate, onClassified, onClose }: Props) {
  const initialProtocol = candidate.protocolHints[0] ?? 'modbus-tcp';
  const [protocol, setProtocol] = useState<string>(initialProtocol);
  const [symbol, setSymbol] = useState<string>(suggestSymbol(candidate));
  const [name, setName] = useState<string>(candidate.hostname ?? candidate.vendorHint ?? `Controller ${candidate.ip}`);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, saving]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (symbol.trim() === '') throw new Error('Symbol darf nicht leer sein.');
      if (name.trim() === '') throw new Error('Anzeigename darf nicht leer sein.');
      const res = await api.post<{ deviceId: string; deviceSymbol: string; deviceName: string; protocol: string }>(
        `/api/plants/${plantId}/scan/candidates/${candidate.id}/classify`,
        { protocol, symbol: symbol.trim(), name: name.trim() });
      onClassified(res.deviceId, res.protocol);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string })?.error;
        setError(bodyMsg ?? `Klassifizieren fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !saving && onClose()}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Kandidat klassifizieren</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen" disabled={saving}>×</button>
        </div>
        <div class="hv-modal__body">
          <p class="muted" style={{ marginTop: 0 }}>
            <span class="mono">{candidate.ip}</span>
            {candidate.mac && <> · MAC <span class="mono">{candidate.mac}</span></>}
            {candidate.hostname && <> · Host <span class="mono">{candidate.hostname}</span></>}
            {candidate.vendorHint && <> · Vendor <span class="mono">{candidate.vendorHint}</span></>}
          </p>
          <p class="muted">
            Der Scanner hat folgende Protokolle vermutet:{' '}
            {candidate.protocolHints.length > 0
              ? candidate.protocolHints.map((p) => <code class="mono" style={{ marginRight: '0.4em' }} key={p}>{p}</code>)
              : <em>keine Hints</em>}
          </p>

          <div class="hv-modal__field">
            <label>Protokoll</label>
            <select value={protocol} onChange={(e) => setProtocol((e.target as HTMLSelectElement).value)}>
              {KNOWN_PROTOCOLS.map((p) => (
                <option key={p} value={p}>{p}{candidate.protocolHints.includes(p) ? ' — vom Scanner erkannt' : ''}</option>
              ))}
            </select>
          </div>
          <div class="hv-modal__field">
            <label>Symbol *</label>
            <input type="text" value={symbol} onInput={(e) => setSymbol((e.target as HTMLInputElement).value)}
                   placeholder="z.B. waermepumpe-01" />
            <small class="muted">
              lowercase, ASCII, dash-separated. Wird gemeinsam mit der Plant-Id in eine
              deterministische Device-Id gehasht — kann nach Anlage nicht mehr geändert werden.
            </small>
          </div>
          <div class="hv-modal__field">
            <label>Anzeigename *</label>
            <input type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)}
                   placeholder="z.B. Wärmepumpe 01" />
          </div>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <div style={{ flex: 1 }} />
          <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
            {saving ? 'Lege an…' : 'Klassifizieren'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
